import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { staffApi } from '../api/mockRepo';
import { Staff } from '../types';
import { StaffFormData } from '../schemas/staff';
import StaffForm from '../components/StaffForm';
import toast from 'react-hot-toast';

const StaffEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [staff, setStaff] = useState<Staff | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!id);

  const isEditing = !!id;

  useEffect(() => {
    if (id) {
      loadStaff();
    }
  }, [id]);

  const loadStaff = async () => {
    if (!id) return;
    
    try {
      setInitialLoading(true);
      const data = await staffApi.getById(id);
      if (!data) {
        toast.error('Staff member not found');
        navigate('/staff');
        return;
      }
      setStaff(data);
    } catch (error) {
      toast.error('Failed to load staff member');
      console.error('Error loading staff:', error);
      navigate('/staff');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (data: StaffFormData) => {
    try {
      setLoading(true);
      
      if (isEditing && id) {
        await staffApi.update(id, data);
        toast.success('Staff member updated successfully');
      } else {
        await staffApi.create(data);
        toast.success('Staff member added successfully');
      }
      
      navigate('/staff');
    } catch (error: any) {
      toast.error(error.message || 'Failed to save staff member');
      console.error('Error saving staff:', error);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/staff')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditing ? 'Edit Staff Member' : 'Add New Staff Member'}
          </h1>
          <p className="text-gray-600">
            {isEditing 
              ? `Update information for ${staff?.name || 'staff member'}`
              : 'Enter staff member details and generate employee code'
            }
          </p>
        </div>
      </div>

      {/* Form */}
      <StaffForm
        initialData={staff || undefined}
        onSubmit={handleSubmit}
        loading={loading}
      />
    </div>
  );
};

export default StaffEditor;
